import { useTranslation } from 'react-i18next';
import type { ImageSourcePropType } from 'react-native';

export type EventHero = 'wine' | 'verde' | 'midnight';

export type EventItem = {
  slug: string;
  ord: string;
  title: string;
  dateLabel: string;
  dateISO?: string;
  location: string;
  guests?: string;
  body: string;
  highlights: string[];
  ticketUrl?: string;
  hero: EventHero;
  featured?: boolean;
  format?: string;
  image?: ImageSourcePropType;
};

type FeaturedStatic = Pick<EventItem, 'hero' | 'featured' | 'ticketUrl' | 'image'>;

const FEATURED_STATIC: Record<string, FeaturedStatic> = {
  'zoukdays-zuerich-2026': {
    hero: 'midnight',
    featured: true,
    ticketUrl: 'https://brazilian-zouk.ch/zoukdays',
    image: require('../../assets/events/zoukdays-zurich.webp'),
  },
  'samba-weekend-2026': {
    hero: 'verde',
    featured: true,
    ticketUrl: 'https://brazilian-zouk.ch/samba-weekend',
    image: require('../../assets/events/samba-weekend.webp'),
  },
};

type FeaturedEntry = Omit<EventItem, 'hero' | 'featured' | 'ticketUrl' | 'image'>;

export function useFeaturedEvents(): EventItem[] {
  const { t } = useTranslation();
  const entries = t('events.featuredItems', { returnObjects: true }) as FeaturedEntry[];
  return entries.map((e) => ({ ...FEATURED_STATIC[e.slug], ...e }));
}

export type FocusDay = { date: string; next?: boolean; topic?: string };

const FOCUS_NEXT_INDEX = 0;

export function useFocusDays(): FocusDay[] {
  const { t } = useTranslation();
  const items = t('events.focusDays.items', { returnObjects: true }) as FocusDay[];
  return items.map((d, i) => (i === FOCUS_NEXT_INDEX ? { ...d, next: true } : d));
}

export type CalendarEntry = {
  date: string;
  title: string;
  format: string;
  url?: string;
  highlighted?: boolean;
};

const CALENDAR_HIGHLIGHT_INDICES = new Set([2, 4]);
const CALENDAR_URLS: Record<number, string> = {
  2: '/events/samba-weekend-2026',
  4: '/events/zoukdays-zuerich-2026',
};

export function useCalendar(): CalendarEntry[] {
  const { t } = useTranslation();
  const items = t('events.kalender.items', { returnObjects: true }) as CalendarEntry[];
  return items.map((c, i) => ({
    ...c,
    ...(CALENDAR_HIGHLIGHT_INDICES.has(i) ? { highlighted: true } : null),
    ...(CALENDAR_URLS[i] ? { url: CALENDAR_URLS[i] } : null),
  }));
}
